import type { RoomResponse } from '../types/auth';

export type EffectiveStatus =
  | 'scheduled'
  | 'live'
  | 'ending_soon'
  | 'ended'
  | 'ended_empty';

export type Phase =
  | 'before_start'
  | 'countdown'
  | 'practice'
  | 'reflection'
  | 'after_planned_end';

export type RoomLifecycleMode = 'timer' | 'open';

export interface EffectiveStatusInputs {
  room: Pick<
    RoomResponse,
    | 'status'
    | 'scheduledAtUtc'
    | 'startedAtUtc'
    | 'endedAtUtc'
    | 'countdownDurationSeconds'
    | 'durationMinutes'
    | 'reflectionDurationMinutes'
    | 'socialPhaseEnabled'
    | 'socialPhaseDurationMinutes'
    | 'videoPracticeDurationMinutes'
    | 'videoPracticeDurationSeconds'
    | 'soundTrackDurationSeconds'
    | 'hostUserId'
    | 'peakParticipantCount'
    | 'isPaused'
    | 'pausedAtUtc'
    | 'totalPausedSeconds'
  > & {
    lifecycleMode?: RoomLifecycleMode;
    viewerCountAllTime?: number;
    hostJoinedAllTime?: boolean;
  };
  participants: Array<{ userId: string; isHost: boolean }> | null;
  liveViewerCountAllTime?: number | null;
  liveHostJoinedAllTime?: boolean | null;
  /**
   * Milliseconds the participants list has been empty (after someone was
   * here). Hook-tracked so R9b can debounce instead of firing on transient
   * SignalR re-broadcasts. `null` means «not currently empty».
   */
  participantsEmptyForMs?: number | null;
  /**
   * Milliseconds the host has been absent from `participants` (after they
   * had joined at least once). Hook-tracked so the host-present override
   * can debounce a brief reconnect — see `HOST_ABSENT_DEBOUNCE_MS`. `null`
   * means «host is currently here OR presence info is unavailable».
   */
  hostAbsentForMs?: number | null;
  now: number;
}

/**
 * R9b debounce window. SignalR can briefly re-broadcast an empty participant
 * list during a media swap or reconnect; without a debounce that 1-frame
 * blip flips the screen permanently into the summary view. 30 s is long
 * enough to clear any reconnect race, short enough that a real abandoned
 * room still closes promptly.
 */
export const R9B_EMPTY_DEBOUNCE_MS = 30_000;

/**
 * Host-present debounce window. While the host is in the room, timer-based
 * auto-end (R3/R5/R6/R7) is disabled — the host closes manually. When the
 * host disconnects briefly (network blip, refresh) we keep the override
 * for this many ms so a 1-frame absence doesn't evict everyone to summary.
 * Reuses the R9b debounce — same threshold, same SignalR reconnect race.
 */
export const HOST_ABSENT_DEBOUNCE_MS = R9B_EMPTY_DEBOUNCE_MS;

/**
 * Presence-bootstrap window. SignalR `JoinParty` is async — between the
 * hook mounting and the first `ParticipantJoined` push, `participants`
 * is `[]`. If the room's `scheduledAtUtc` is already past (BE clock skew,
 * room scheduled for «just now», host re-entered after a refresh), phase
 * rules R3/R7 could fire and evict the host before the hub finishes its
 * first broadcast — owner-reported 2026-05-08 «практика завершилась за
 * несколько секунд».
 *
 * For 15 s of uninterrupted empty presence at mount, treat the room as
 * effectively-live (skip phase rules). After that, if presence is still
 * empty, R9a/R9b take over normally — so genuinely abandoned rooms still
 * get cleaned up. R0 (BE Ended) wins above this window — if BE confirms
 * end, FE shows summary regardless.
 */
export const PRESENCE_BOOTSTRAP_MS = 15_000;

export interface EffectiveStatusResult {
  effectiveStatus: EffectiveStatus;
  secondsUntilAutoEnd: number | null;
}

// Session (practice) length in seconds for lifecycle math. AMB-47: prefer
// `durationMinutes` — the master's chosen session length. That is the SAME
// value the BE auto-end cron uses and that LiveRoomScreen's own `totalSeconds`
// already prefers; the media-file length is only a fallback for legacy rows
// that never carried `durationMinutes`.
//
// Reading the media-file length *first* was the recurring «эфир завершился
// сам ≈ через минуту» bug (AMB-47, rooms 170/181/186/187/189/195): a
// VideoPractice room whose DTO omitted `videoPracticeDurationMinutes` (but
// carried `durationMinutes`) resolved to 0, so `computeVideoEnd` /
// `computePlannedEnd` collapsed to `scheduledAt + countdown`. R7 then flipped
// `effectiveStatus → 'ended'` ~countdown seconds after the scheduled start
// and LiveRoomScreen rendered the summary, while the BE kept the room Active
// for the full duration. Telemetry on room 195 (7zOSwH): FE summary at
// scheduledAt+60s, BE `EndedAtUtc` at `StartedAtUtc+60s+4m practice+10m
// social`. The previous AMB-47 fixes all touched the LiveRoomScreen
// `sessionPhase`/`computeSessionElapsed` state machine — never this file,
// which is what actually gates the summary screen (LiveRoomScreen R0/R7).
export function getSessionDurationSeconds(
  room: Pick<
    EffectiveStatusInputs['room'],
    | 'durationMinutes'
    | 'videoPracticeDurationMinutes'
    | 'videoPracticeDurationSeconds'
    | 'soundTrackDurationSeconds'
  >,
): number {
  if (typeof room.durationMinutes === 'number' && room.durationMinutes > 0) {
    return room.durationMinutes * 60;
  }
  // CORE-1285 — длина практики известна в секундах; минуты у неё округлены
  // ВВЕРХ, поэтому комната без своей длительности наследовала «минуту» от клипа
  // в две секунды. Точное значение идёт первым, минуты остаются фолбэком для
  // строк, до которых не дошёл бэкфилл, и для старого бэкенда.
  if (typeof room.videoPracticeDurationSeconds === 'number' && room.videoPracticeDurationSeconds > 0) {
    return room.videoPracticeDurationSeconds;
  }
  if (typeof room.videoPracticeDurationMinutes === 'number' && room.videoPracticeDurationMinutes > 0) {
    return room.videoPracticeDurationMinutes * 60;
  }
  if (typeof room.soundTrackDurationSeconds === 'number' && room.soundTrackDurationSeconds > 0) {
    return room.soundTrackDurationSeconds;
  }
  return 0;
}

// Post-practice tail (the «reflection» / «social» window) in seconds. Mirrors
// the master-intent prefer-chain LiveRoomScreen uses for `reflMin`: an enabled
// social phase wins over the legacy reflection window. Keeping this in
// lock-step with the BE end math is what stops the FE summary from
// pre-empting a still-Active room (room 195: BE tail = 10m social, not the
// 0m `reflectionDurationMinutes`).
export function getPostPracticeTailSeconds(
  room: Pick<
    EffectiveStatusInputs['room'],
    'socialPhaseEnabled' | 'socialPhaseDurationMinutes' | 'reflectionDurationMinutes'
  >,
): number {
  const tailMin =
    room.socialPhaseEnabled && (room.socialPhaseDurationMinutes ?? 0) > 0
      ? room.socialPhaseDurationMinutes
      : room.reflectionDurationMinutes;
  return Math.max(0, (tailMin ?? 0) * 60);
}

export function computeVideoEnd(
  room: Pick<
    EffectiveStatusInputs['room'],
    | 'countdownDurationSeconds'
    | 'durationMinutes'
    | 'videoPracticeDurationMinutes'
    | 'videoPracticeDurationSeconds'
    | 'soundTrackDurationSeconds'
  >,
  startMs: number,
): number {
  return startMs + (room.countdownDurationSeconds + getSessionDurationSeconds(room)) * 1000;
}

export function computePlannedEnd(
  room: Pick<
    EffectiveStatusInputs['room'],
    | 'countdownDurationSeconds'
    | 'durationMinutes'
    | 'videoPracticeDurationMinutes'
    | 'videoPracticeDurationSeconds'
    | 'soundTrackDurationSeconds'
    | 'reflectionDurationMinutes'
    | 'socialPhaseEnabled'
    | 'socialPhaseDurationMinutes'
  >,
  startMs: number,
): number {
  return computeVideoEnd(room, startMs) + getPostPracticeTailSeconds(room) * 1000;
}

/**
 * Pause-aware time shift for phase calculations. BE freezes its auto-end
 * cron while `isPaused=true` and tracks accumulated pause in
 * `totalPausedSeconds`. Without mirroring this on the FE, the host pausing
 * a live-led session would still see the room flip to `reflection` /
 * `after_planned_end` on wall-clock time and get evicted by R3/R7 (the
 * «свама» bug — host alone + paused → phase moves on without them).
 *
 * Only applied to phase-driven rules (R2/R3/R5/R6/R7). R9a/R9b stay on
 * real clock so a forgotten paused empty room still gets cleaned up.
 */
export function computePauseShiftMs(
  room: Partial<Pick<RoomResponse, 'isPaused' | 'pausedAtUtc' | 'totalPausedSeconds'>>,
  now: number,
): number {
  const accumulated = (room.totalPausedSeconds ?? 0) * 1000;
  if (room.isPaused && room.pausedAtUtc) {
    const pausedAtMs = new Date(room.pausedAtUtc).getTime();
    if (Number.isFinite(pausedAtMs) && pausedAtMs <= now) {
      return accumulated + (now - pausedAtMs);
    }
  }
  return accumulated;
}

export function computePhase(
  room: Pick<
    EffectiveStatusInputs['room'],
    | 'countdownDurationSeconds'
    | 'durationMinutes'
    | 'videoPracticeDurationMinutes'
    | 'videoPracticeDurationSeconds'
    | 'soundTrackDurationSeconds'
    | 'reflectionDurationMinutes'
    | 'socialPhaseEnabled'
    | 'socialPhaseDurationMinutes'
  >,
  startMs: number,
  now: number,
): Phase {
  if (now < startMs) return 'before_start';
  const countdownEnd = startMs + room.countdownDurationSeconds * 1000;
  if (now < countdownEnd) return 'countdown';
  const videoEnd = computeVideoEnd(room, startMs);
  if (now < videoEnd) return 'practice';
  const plannedEnd = computePlannedEnd(room, startMs);
  if (now < plannedEnd) return 'reflection';
  return 'after_planned_end';
}

function resolveAggregates(inputs: EffectiveStatusInputs): {
  viewerCountAllTime: number;
  hostJoinedAllTime: boolean;
} {
  const room = inputs.room;
  const viewerCountAllTime =
    inputs.liveViewerCountAllTime != null
      ? inputs.liveViewerCountAllTime
      : room.viewerCountAllTime ??
        (typeof room.peakParticipantCount === 'number'
          ? Math.max(0, room.peakParticipantCount - (room.hostJoinedAllTime ? 1 : 0))
          : 0);
  const hostJoinedAllTime =
    inputs.liveHostJoinedAllTime != null
      ? inputs.liveHostJoinedAllTime
      : room.hostJoinedAllTime ?? false;
  return { viewerCountAllTime, hostJoinedAllTime };
}

/**
 * «The host is effectively still in the room.» AMB-47 fix: this must gate
 * the empty-room rules (R9a/R9b), not just the timer rules — the original
 * code computed it AFTER R9a/R9b, so a solo host whose own SignalR
 * `ParticipantJoined` never echoed (hub `participants` stays `[]`) was
 * declared an «empty room» 60 s after scheduled (R9a) / after 30 s empty
 * (R9b) and evicted to the summary, while the BE — which has the host as a
 * participant (`LeftAt` null, `HostJoinedAllTime=true`) — kept the room
 * Active for the full lifecycle (room 195/7zOSwH, «эфир завершился сам ≈
 * через минуту»). Mirrors the BE cron's masterStillIn invariant.
 *
 * Sources of truth for «host has joined», in order of reliability:
 *   • host currently in `participants` (live SignalR snapshot);
 *   • host gone but within the absent-debounce (brief reconnect);
 *   • sticky: `liveHostJoinedAllTime` OR the BE-aggregated
 *     `room.hostJoinedAllTime` (live context only), while no absent-clock
 *     has started. The local trace alone is fragile — a master who joined
 *     before the viewer never produces a `ParticipantJoined` for the
 *     viewer, so it stays false; the BE flag is authoritative.
 *
 * Live context only (`participants !== null`). Dashboard
 * (`participants === null`) deliberately returns `false` so R9a/R9b/R7
 * still drive zombie cleanup of rooms whose host forgot to click
 * «Завершить».
 */
export function resolveHostStillEffective(
  inputs: Pick<
    EffectiveStatusInputs,
    'participants' | 'liveHostJoinedAllTime' | 'hostAbsentForMs'
  > & { room: Pick<EffectiveStatusInputs['room'], 'hostUserId' | 'hostJoinedAllTime'> },
): boolean {
  const { room } = inputs;
  const hostInParticipants =
    inputs.participants !== null && room.hostUserId
      ? inputs.participants.some((p) => p.userId === room.hostUserId)
      : false;
  const hostInDebounce =
    inputs.hostAbsentForMs != null &&
    inputs.hostAbsentForMs < HOST_ABSENT_DEBOUNCE_MS;
  const isLiveContext = inputs.participants !== null;
  const beHostHasJoined = room.hostJoinedAllTime === true;
  const hostStickyPresent =
    (inputs.liveHostJoinedAllTime === true ||
      (isLiveContext && beHostHasJoined)) &&
    inputs.hostAbsentForMs == null;
  return hostInParticipants || hostInDebounce || hostStickyPresent;
}

/**
 * Server-confirmed end — the ONLY thing a participant-facing surface may use
 * to decide «show the summary / завершено screen». This is R0 as a standalone
 * predicate.
 *
 * The rest of `computeEffectiveStatus` is a heuristic: with no live presence
 * (`participants: null` on the entry screen, or `[]` during the SignalR
 * handshake on cold deep-link entry) the timer rules R7/R9a can collapse an
 * Active room to 'ended'/'ended_empty' — that is deliberate for dashboard
 * zombie-cleanup, but it is a FALSE terminal signal for someone opening a
 * direct link to a still-running session (CORE-812, the AMB-47 family: rooms
 * 170/181/186/187/189/195). The BE keeps such a room Active for its full
 * lifecycle; only `status === 'Ended'` / `endedAtUtc` are authoritative.
 *
 * The real end always produces one of these two on the FE too — the hub's
 * `PartyEnded`/`onRoomEnded` handlers set `status: 'Ended'` + `endedAtUtc`
 * (LiveRoomScreen), and `getRoomByCode` returns them for an already-closed
 * room — so gating the summary on this predicate never hides a genuine end.
 */
export function isServerConfirmedEnded(
  room: Pick<RoomResponse, 'status' | 'endedAtUtc'> | null | undefined,
): boolean {
  if (!room) return false;
  return room.status === 'Ended' || !!room.endedAtUtc;
}

/**
 * Начало комнаты для всех правил ниже: расписание, а если его нет — момент,
 * когда сессия фактически стартовала.
 *
 * CORE-1370 (возврат QA 21.09.2026). У комнаты «Начать сейчас»
 * `scheduledAtUtc` пуст — её никто не планировал, она началась по нажатию.
 * Отсчёт брался только из расписания, поэтому R1 возвращал `scheduled` для
 * живой сессии ещё до ветки `open` и до правил присутствия: на деве комната
 * 4687 (`e4xcM9`, open, `startedAtUtc` 13:19:00) отдавала в
 * `live_session_entry_viewed` `status: scheduled`, а контрольная 4686 с
 * расписанием — правильный `live`. Роль тут ни при чём: снимок на входном
 * экране один и тот же (`getRoomByCode`) и у гостя, и у участника.
 *
 * Запасная точка определена всегда, когда нужна: комнаты без расписания
 * заводятся уже запущенными (на деве таких 1983, `startedAtUtc` стоит у всех
 * до единой), поэтому `null` остаётся только у по-настоящему незапущенной
 * комнаты — она и есть `scheduled`.
 *
 * Расписание сильнее старта намеренно: у запланированной комнаты
 * `startedAtUtc` может оказаться РАНЬШЕ него (та же 4686: старт 13:13:16 при
 * расписании на 13:15), а фазовые правила должны считаться от объявленного
 * начала, как считались до сих пор.
 */
export function resolveStartAnchorMs(
  room: Pick<EffectiveStatusInputs['room'], 'scheduledAtUtc' | 'startedAtUtc'>,
): number | null {
  if (room.scheduledAtUtc) return new Date(room.scheduledAtUtc).getTime();
  if (room.startedAtUtc) {
    const startedMs = new Date(room.startedAtUtc).getTime();
    return Number.isFinite(startedMs) ? startedMs : null;
  }
  return null;
}

export function computeEffectiveStatus(
  inputs: EffectiveStatusInputs,
): EffectiveStatusResult {
  const { room, now } = inputs;

  // R0 — BE-confirmed Ended.
  if (isServerConfirmedEnded(room)) {
    return { effectiveStatus: 'ended', secondsUntilAutoEnd: null };
  }

  const startMs = resolveStartAnchorMs(room);

  // R1 — до начала комнаты: расписание ещё не наступило, а фактического
  // старта не было (`resolveStartAnchorMs` — CORE-1370).
  if (startMs === null || now < startMs) {
    return { effectiveStatus: 'scheduled', secondsUntilAutoEnd: null };
  }

  // Presence-bootstrap (checked BEFORE R9a/R9b/phase rules). If the live
  // hook just mounted and SignalR hasn't yet pushed the first participants
  // snapshot, we're in a startup race. Treat the room as live for the
  // bootstrap window so a host who entered seconds ago doesn't get evicted
  // by R9a/R7 before the hub finishes handshaking. After the window
  // expires, normal rules resume — genuinely abandoned rooms still clean
  // up via R9a, and BE-confirmed Ended (R0) wins above this anyway.
  //
  // Requires `participantsEmptyForMs` to be an explicit number — only the
  // hook sets it (it tracks the emptiness clock). Dashboard surfaces and
  // tests that omit the field skip the bootstrap and fall through to the
  // normal rules, preserving zombie cleanup.
  if (
    inputs.participants !== null &&
    inputs.participants.length === 0 &&
    inputs.liveHostJoinedAllTime !== true &&
    typeof inputs.participantsEmptyForMs === 'number' &&
    inputs.participantsEmptyForMs < PRESENCE_BOOTSTRAP_MS
  ) {
    return { effectiveStatus: 'live', secondsUntilAutoEnd: null };
  }

  // Host-present override is evaluated BEFORE the empty-room rules
  // (AMB-47): while the host is effectively still here — including via the
  // BE-authoritative `room.hostJoinedAllTime` — R9a/R9b must not declare
  // the room empty. Dashboard (`participants === null`) → `false`, so
  // zombie cleanup is unaffected.
  const hostStillEffective = resolveHostStillEffective(inputs);

  // R9a — empty room 60s after start (any mode), unless the host
  // effectively showed up (BE/SignalR).
  const { viewerCountAllTime, hostJoinedAllTime } = resolveAggregates(inputs);
  if (
    now >= startMs + 60_000 &&
    viewerCountAllTime === 0 &&
    !hostJoinedAllTime &&
    !hostStillEffective
  ) {
    return { effectiveStatus: 'ended_empty', secondsUntilAutoEnd: 0 };
  }

  // R9b — everyone (host + viewers) physically left after someone had been
  // there. Debounced — only fires once the empty state has held for the
  // full window. Without the debounce, a transient empty broadcast (media
  // swap, reconnect) would flip the screen into a permanent summary view
  // and steal the session from the host. `!hostStillEffective` keeps a
  // flaky-presence solo host (BE still has them) from being evicted
  // (AMB-47, room 195).
  if (
    inputs.participants !== null &&
    inputs.participants.length === 0 &&
    (viewerCountAllTime > 0 || hostJoinedAllTime) &&
    (inputs.participantsEmptyForMs ?? 0) >= R9B_EMPTY_DEBOUNCE_MS &&
    !hostStillEffective
  ) {
    return { effectiveStatus: 'ended', secondsUntilAutoEnd: 0 };
  }

  // Host-present override (computed above as `hostStillEffective` so it
  // also gates R9a/R9b — see `resolveHostStillEffective`). An 'open'
  // lifecycle room, or one where the host is effectively still here, never
  // auto-ends on the timer — the host owns when it ends (owner directive
  // 2026-05-07: «когда мастер внутри комнаты с самого начала, таймер не
  // нужен»).
  const isOpen =
    (room.lifecycleMode ?? 'timer') === 'open' || hostStillEffective;
  if (isOpen) {
    return { effectiveStatus: 'live', secondsUntilAutoEnd: null };
  }

  // Timer mode — phase-driven. Phase clock is shifted by accumulated +
  // current pause so a paused room doesn't sail past video-end / planned-end.
  const pauseShiftMs = computePauseShiftMs(room, now);
  const effectiveNow = now - pauseShiftMs;
  const phase = computePhase(room, startMs, effectiveNow);

  if (phase === 'countdown' || phase === 'practice') {
    return { effectiveStatus: 'live', secondsUntilAutoEnd: null };
  }

  if (phase === 'reflection') {
    // R3 — skip reflection with ≤1 viewer.
    if (viewerCountAllTime <= 1) {
      return { effectiveStatus: 'ended_empty', secondsUntilAutoEnd: 0 };
    }
    const plannedEnd = computePlannedEnd(room, startMs);
    const secondsUntilAutoEnd = Math.max(0, Math.floor((plannedEnd - effectiveNow) / 1000));
    if (secondsUntilAutoEnd <= 120) {
      return { effectiveStatus: 'ending_soon', secondsUntilAutoEnd };
    }
    return { effectiveStatus: 'live', secondsUntilAutoEnd };
  }

  // R7 — phase = after_planned_end.
  return { effectiveStatus: 'ended', secondsUntilAutoEnd: 0 };
}
